import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import ActionBar from "../../common/ActionBar";
import {
  PageWrapper,
  TopRow,
  StepCard,
  StepCardHeader,
  CardContent,
  StyledForm,
} from "../Field Mapping";
import { CustomFileUpload } from "../../common/FormInputs";
import { PrimaryCTAButton } from "../../common/Buttons";
import TranscriberService from "../../../services/TranscriberService";
import { s3Upload } from "../../common/FileUploadS3";
import useLoader from "../../../hooks/useLoader";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import useSettings from "../../../hooks/useSettings";
import _ from "lodash";
import { navigate } from "@reach/router";

const Transcription = () => {
  const { setting } = useSettings();
  const [transcription, setTranscription] = useState(null);
  const [copied, setCopied] = useState(false);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "transcribers":
        navigate("/dashboard/case-manager/transcriber");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: `Transcribing audio... Please wait` });
      const payload = {
        url: values?.url,
        transcriptionKey:
          setting?.transcriptionAuthToken ||
          "bb14c04d98b3350fa6d62f596d6a8c5452c698a2",
      };
      const response = await TranscriberService.transcriptionDG(payload);
      if (response.transcription) {
        setTranscription(response);
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Transcription failed", { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const transcriptBlocks =
    transcription?.transcription?.results.channels[0].alternatives[0].paragraphs
      .paragraphs || [];

  const fullText = transcriptBlocks
    .map((block) => {
      const text =
        block.sentences
          ?.map((s) => s.text)
          .join(" ")
          .trim() || "";
      if (!text) return "";
      return `Speaker ${block.speaker}:\n\n${text}\n`;
    })
    .filter(Boolean)
    .join("\n");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      enqueueSnackbar("Copy failed", { variant: "error" });
    }
  };

  return (
    <React.Fragment>
      <ActionBar
        breadcrumbs={["Transcribers", "Transcription"]}
        {...{ onBreadcrumbClick }}
      />
      <PageWrapper>
        <Formik
          initialValues={{ url: "" }}
          enableReinitialize
          onSubmit={onFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <StyledForm>
              <TopRow>
                <StepCard style={{ flex: 1 }}>
                  <StepCardHeader>Upload Audio</StepCardHeader>
                  <CardContent>
                    <CustomFileUpload
                      accept="audio/*"
                      file={{ name: values.url }}
                      setFile={(file) =>
                        s3Upload(
                          file,
                          "url",
                          setFieldValue,
                          ["audio/*"],
                          setLoader
                        )
                      }
                      style={{ width: "100%", maxWidth: 420 }}
                    />
                  </CardContent>
                </StepCard>
                <StepCard style={{ flex: 1 }}>
                  <StepCardHeader>
                    After uploading, click here to get the transcription.
                  </StepCardHeader>
                  <CardContent>
                    <PrimaryCTAButton
                      style={{ width: "210px", marginBottom: 10 }}
                      type="submit"
                      disabled={!values.url}
                    >
                      Submit
                    </PrimaryCTAButton>
                  </CardContent>
                </StepCard>
              </TopRow>
            </StyledForm>
          )}
        </Formik>

        <PageContainer>
          {transcriptBlocks.length > 0 && (
            <CopyButtonContainer>
              <CopyButton onClick={handleCopy}>
                {copied ? "Copied!" : "Copy Transcript"}
              </CopyButton>
            </CopyButtonContainer>
          )}
          <TranscriptContainer>
            {transcriptBlocks.length === 0 ? (
              <EmptyState>No transcript available.</EmptyState>
            ) : (
              transcriptBlocks.map((block, index) => {
                const text =
                  block.sentences
                    ?.map((s) => s.text)
                    .join(" ")
                    .trim() || "<No content>";
                return (
                  <SpeakerBlock key={index}>
                    <SpeakerLabel>Speaker {block.speaker}</SpeakerLabel>
                    <SpeakerText>{text}</SpeakerText>
                  </SpeakerBlock>
                );
              })
            )}
          </TranscriptContainer>
        </PageContainer>
      </PageWrapper>
    </React.Fragment>
  );
};

export default Transcription;

const PageContainer = styled.div`
  width: 95%;
  margin: 1rem auto;
  padding: 0 1rem;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const TranscriptContainer = styled.div`
  min-height: 60vh;
  max-height: 70vh;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background: #f8fafc;
  padding: 1.5rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);

  scrollbar-width: thin;
  scrollbar-color: #bdc3c7 #f1f2f6;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f2f6;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: #bdc3c7;
    border-radius: 10px;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    max-height: 60vh;
  }
`;

const SpeakerBlock = styled.div`
  margin-bottom: 2rem;
  padding: 1.2rem;
  border-radius: 12px;
  background: #e3f2fd;
  border-left: 6px solid #00838c;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const SpeakerLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 1rem;
  color: ${COLORS.COLOR_DARK};
  margin-bottom: 0.8rem;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const SpeakerText = styled.p`
  margin: 0;
  line-height: 1.6;
  font-family: ${theme.fonts.primaryFontRegular};
  color: #293461;
  font-size: 0.95rem;
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  font-size: 1.1rem;
  padding: 3rem 1rem;
`;

const CopyButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  background: #00838c;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(52, 152, 219, 0.3);

  &:hover {
    background: #00838c;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    font-size: 0.95rem;
    padding: 10px;
  }
`;
